import SearchIcon from "@mui/icons-material/Search";
import {
	Button,
	Grid2,
	IconButton,
	InputAdornment,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useState } from "react";
import { ErrorModal } from "../../../../components/modals/error-modal";
import { CenteredLazyLoading } from "../../../../components/shared/CenteredLazyLoading";
import { useContextMenuMESF } from "../../../../controls/contextMenu";
import { themeDXT } from "../../../../css/themeMESF";
import { getUTLSettings } from "../../../../repositorie/MESF-frontend-Repositorie";
import { Setting } from "../models/Setting";
import { useSettingsOptionsFunctions } from "./customHooks/useSettingsOptionsFunctions";
import { useTableData } from "./customHooks/useTableSettings";
import { CreateSetting } from "./upsertDelete/CreateSetting";
import { DeleteSetting } from "./upsertDelete/DeleteSetting";
import { EditSetting } from "./upsertDelete/EditSetting";

const useSearchSettings = () => {
	return useQuery<Setting[], Error>({
		queryKey: ["settings"],
		queryFn: ({ signal }) => getUTLSettings(signal),
	});
};

export const TableSettings = () => {
	const queryClient = useQueryClient();
	const { showContextMenu, registerConfig } = useContextMenuMESF();

	const [error, setError] = useState("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [filterValue, setFilterValue] = useState("");
	const [settingSelected, setSettingSelected] = useState<Setting | null>(null);
	const [openModalNew, setOpenModalNew] = useState(false);
	const [openModalEditSetting, setOpenModalEditSetting] = useState(false);
	const [openModalDeleteSetting, setOpenModalDeleteSetting] = useState(false);

	const { data: rows, isLoading, isError, error: e } = useSearchSettings();

	const { getMenuOptions } = useSettingsOptionsFunctions({
		setSettingId: setSettingSelected,
		setShowDeleteModal: setOpenModalDeleteSetting,
		setShowCreateModal: setOpenModalNew,
		setShowEditModal: setOpenModalEditSetting,
	});

	const { columnDefs, defaultColDef } = useTableData({
		setOpenModalEditSetting,
		setOpenModalDeleteSetting,
		showContextMenu,
		setSettingSelected,
	});

	const getContextMenuItems = (
		params: GetContextMenuItemsParams,
	): (DefaultMenuItem | MenuItemDef)[] => {
		const data = params.node?.data;
		params.api.deselectAll();
		params.node?.setSelected(true);
		showContextMenu(event as any, data, "TableSettings");
		return [];
	};

	useEffect(() => {
		registerConfig({
			id: "TableSettings",
			getOptions: getMenuOptions,
		});
	}, []);

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	return (
		<>
			<Grid2 container justifyContent="center" p={1} spacing={1}>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						Settings
					</Typography>
				</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<TextField
						fullWidth
						label="Search"
						variant="outlined"
						size="small"
						margin="dense"
						value={filterValue}
						onChange={(event: any) => {
							setFilterValue(event.target.value);
							gridApi?.setGridOption("quickFilterText", event.target.value);
						}}
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position="start">
										<IconButton edge="start" type="submit">
											<SearchIcon />
										</IconButton>
									</InputAdornment>
								),
							} as any,
						}}
					/>
				</Grid2>

				<Grid2 container size={{ md: 12, xs: 12 }}>
					<Grid2
						size={{ md: 12, xs: 12 }}
						style={{
							height: "70vh",
						}}
					>
						<Paper style={{ height: "100%", width: "100%" }}>
							<AgGridReact
								loading={isLoading}
								gridOptions={{
									theme: themeDXT,
								}}
								rowData={rows}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								rowHeight={34}
								headerHeight={34}
								animateRows={true}
								loadingOverlayComponent={CenteredLazyLoading}
								getContextMenuItems={(e: any) => getContextMenuItems(e)}
								rowSelection="single"
								onRowDoubleClicked={(event) => {
									setSettingSelected(event.data as Setting);
									setOpenModalEditSetting(true);
								}}
								onGridReady={(params) => setGridApi(params.api)}
							/>
						</Paper>
					</Grid2>
					<Grid2 container justifyContent="flex-end">
						<Grid2 size={{ md: 2, xs: 12 }}>
							<Button
								variant="contained"
								color="primary"
								onClick={() => setOpenModalNew(!openModalNew)}
								fullWidth
							>
								NEW SETTING
							</Button>
						</Grid2>
					</Grid2>
				</Grid2>
			</Grid2>
			<CreateSetting
				settingSelected={settingSelected}
				show={openModalNew}
				onHide={async (shouldUpdate: boolean) => {
					setOpenModalNew(false);
					if (shouldUpdate)
						await queryClient.invalidateQueries({ queryKey: ["settings"] });
				}}
				suffixTitle="Settings"
			/>
			<EditSetting
				settingSelected={settingSelected}
				show={openModalEditSetting}
				onHide={async (shouldUpdate: boolean) => {
					setOpenModalEditSetting(false);
					if (shouldUpdate)
						await queryClient.invalidateQueries({ queryKey: ["settings"] });
				}}
				suffixTitle="Settings"
			/>
			<DeleteSetting
				settingSelected={settingSelected}
				show={openModalDeleteSetting}
				onHide={async (shouldUpdate: boolean) => {
					setOpenModalDeleteSetting(false);
					if (shouldUpdate)
						await queryClient.invalidateQueries({ queryKey: ["settings"] });
				}}
				suffixTitle="Settings"
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
